import { useCallback } from 'react';

import { useSafeFocusEffect, useSafeRouteParams, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useLastInteractedStore } from '../../context/LastInteractedStore';
import { useIsCustomerAddress } from '../../hooks';

import type { MenuWithoutSlugScreenProps } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A "placeholder" route that will always redirect users to the menu or
 * delivery menu route if the location slug or address ID is available,
 * or to the location selection route if it is not.
 */
export const MenuWithoutSlug = (props: MenuWithoutSlugProps) => {
  const { navigation } = props;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();

  const lastInteractedStore = useLastInteractedStore();
  const { restaurantSlug, addressId } = lastInteractedStore ?? {};

  const isCustomerAddress = useIsCustomerAddress(addressId);

  const routeParams = useSafeRouteParams<{ category?: string }>();
  const category = routeParams?.category;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const redirectToMenuOrLocation = useCallback(() => {
    const hasValidMenuRouteSlug = restaurantSlug !== undefined;
    const hasValidDeliveryMenuRouteSlug = addressId && isCustomerAddress;

    // if the last interacted store is a delivery location, navigate to the
    // associated delivery menu screen
    if (hasValidDeliveryMenuRouteSlug) {
      if (isExpoRouterEnabled) {
        router?.replace({ pathname: '/delivery/[addressId]/menu', params: { addressId, ...(category ? { category } : {}) } }); // prettier-ignore
        return;
      }

      navigation?.replace('DeliveryMenu', { addressId, category });

      return;
    }

    // if the last interacted store is a pickup or outpost location, navigate
    // to the associated menu screen
    if (hasValidMenuRouteSlug) {
      if (isExpoRouterEnabled) {
        router?.replace({ pathname: '/[restaurantSlug]/menu', params: { restaurantSlug, ...(category ? { category } : {}) } }); // prettier-ignore
        return;
      }

      navigation?.replace('Menu', { restaurantSlug, category });

      return;
    }

    // otherwise navigate to the location search screen
    if (isExpoRouterEnabled) {
      router?.replace({ pathname: '/locations', params: category ? { category } : {} });
      return;
    }

    navigation?.replace('Locations', { category });
  }, [addressId, category, isCustomerAddress, isExpoRouterEnabled, navigation, restaurantSlug, router]); // prettier-ignore

  // ─── Effects ─────────────────────────────────────────────────────────

  useSafeFocusEffect(redirectToMenuOrLocation);

  // ─────────────────────────────────────────────────────────────────────

  return null;
};

// ─── Types ────────────────────────────────────────────────────────────────────

type MenuWithoutSlugProps = Partial<MenuWithoutSlugScreenProps>;
